
    import { isTypingInExternalInput } from "@utils/keyboard";

    // Client-side keyboard navigation for the device catalog modal.
    //
    // Focus stays on real DOM elements (the row overlay buttons) so Tab and
    // Shift-Tab continue to walk into the expanded preferences panel
    // naturally. The server never sees navigation keys — only the existing
    // phx-click events that real button presses already emit.
    //
    // DOM contract:
    //   data-keynav-scroll : scroll container that holds the rows
    //   data-keynav-row    : the focusable row button (one per row)
    //   data-keynav-tab    : the segmented control's tab buttons
    //   data-keynav-input  : the search input above the rows
    const NAV_KEYS = new Set([
      "ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight",
      "j", "k", "h", "l",
      "Home", "End", "g", "G",
    ]);

    function rows(el) {
      const scroll = el.querySelector("[data-keynav-scroll]");
      if (!scroll) return [];
      return Array.from(scroll.querySelectorAll("[data-keynav-row]"));
    }

    function currentIndex(allRows) {
      const active = document.activeElement;
      return allRows.findIndex((r) => r === active || r.contains(active));
    }

    function focusRow(row) {
      if (!row) return;
      row.focus({ preventScroll: true });
      row.scrollIntoView({ block: "nearest" });
    }

    function moveTo(allRows, index) {
      if (allRows.length === 0) return;
      const clamped = Math.max(0, Math.min(allRows.length - 1, index));
      focusRow(allRows[clamped]);
    }

    function switchTab(el, direction) {
      const tabs = Array.from(el.querySelectorAll("[data-keynav-tab]"));
      if (tabs.length < 2) return false;
      const pressed = tabs.findIndex((t) => t.getAttribute("aria-pressed") === "true");
      const next = (pressed + direction + tabs.length) % tabs.length;
      tabs[next].click();
      return true;
    }

    export default {
      mounted() {
        this._gPressed = false;
        this._gTimer = null;

        this._onKeydown = (e) => {
          if (!NAV_KEYS.has(e.key)) return;
          if (isTypingInExternalInput(this.el)) return;

          // Ignore arrow keys when focus is inside a form control (so the
          // preference-form selects / inputs keep their native behavior).
          const active = document.activeElement;
          if (active && active !== this.el) {
            const tag = active.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
            if (active.isContentEditable) return;
          }

          const allRows = rows(this.el);

          switch (e.key) {
            case "ArrowDown":
            case "j": {
              e.preventDefault();
              const idx = currentIndex(allRows);
              moveTo(allRows, idx === -1 ? 0 : idx + 1);
              break;
            }

            case "ArrowUp":
            case "k": {
              e.preventDefault();
              const idx = currentIndex(allRows);
              moveTo(allRows, idx === -1 ? 0 : idx - 1);
              break;
            }

            case "ArrowLeft":
            case "h": {
              if (switchTab(this.el, -1)) e.preventDefault();
              break;
            }

            case "ArrowRight":
            case "l": {
              if (switchTab(this.el, +1)) e.preventDefault();
              break;
            }

            case "Home": {
              e.preventDefault();
              moveTo(allRows, 0);
              break;
            }

            case "End":
            case "G": {
              e.preventDefault();
              moveTo(allRows, allRows.length - 1);
              break;
            }

            case "g": {
              // vim-style `gg` → jump to first row
              if (this._gPressed) {
                e.preventDefault();
                this._gPressed = false;
                clearTimeout(this._gTimer);
                moveTo(allRows, 0);
              } else {
                this._gPressed = true;
                clearTimeout(this._gTimer);
                this._gTimer = setTimeout(() => (this._gPressed = false), 500);
              }
              break;
            }
          }
        };

        this.el.addEventListener("keydown", this._onKeydown);

        // Autofocus the first available row so subsequent Tab and ArrowDown
        // start from a known anchor. Defer so the LiveView swap completes.
        requestAnimationFrame(() => {
          const first = rows(this.el)[0];
          if (first && !this.el.contains(document.activeElement)) {
            focusRow(first);
          }
        });
      },

      destroyed() {
        this.el.removeEventListener("keydown", this._onKeydown);
        clearTimeout(this._gTimer);
      },
    };
  